import { isCanonicalUuid } from '@automatick/shared-client-ts';
import {
  WorkspaceAutonomyMode,
  WorkspaceKind,
  WorkspaceMemberStatus,
  WorkspaceRole,
  type UserContextResponse,
  type UserSettingsResponse,
  type WorkspaceSummaryResponse,
} from '@automatick/shared-ng';
import type { Observable, Subscription } from 'rxjs';
import type {
  WorkspaceBootstrapState,
  WorkspaceBootstrapUser,
  WorkspaceBootstrapUserSettings,
  WorkspaceBootstrapWorkspace,
  WorkspaceAgentConfirmationMode,
} from './app-state.service';

const LOAD_ERROR = 'Failed to load account/workspace context.';
const SWITCH_ERROR = 'Failed to switch workspace.';
const RETRY_COOLDOWN_MS = 5000;
const MAX_TIME_CLIP = 8.64e15;
const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';
const isoOffsetTimestampPattern =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,7})?(?:Z|([+-]\d{2}:\d{2}))$/u;
const NO_AUTHORITY = Object.freeze({
  activeWorkspaceId: null,
  activeRole: null,
  activeWorkspaceType: null,
});

export interface AppWorkspaceContextResponse {
  readonly user: UserContextResponse;
  readonly workspaces: readonly WorkspaceSummaryResponse[];
}

export interface AppWorkspaceContextPort {
  fetchContext(): Observable<AppWorkspaceContextFetchResult>;
  switchWorkspace(workspaceId: string): Observable<unknown>;
}

export type AppWorkspaceContextFetchResult =
  | {
      readonly kind: 'context';
      readonly response: AppWorkspaceContextResponse;
    }
  | { readonly kind: 'unauthenticated' };

export interface AppWorkspacePreferencePort {
  read(): unknown;
  write(workspaceId: string): void;
  remove(): void;
}

export interface AppWorkspaceContextEnvironment {
  now(): number;
}

export interface AppWorkspaceContextSink {
  publish(state: WorkspaceBootstrapState): void;
}

interface DecodedWorkspaceContext {
  readonly user: WorkspaceBootstrapUser;
  readonly workspaces: readonly WorkspaceBootstrapWorkspace[];
  readonly currentWorkspaceId: string | null;
  readonly defaultWorkspaceId: string | null;
}

interface OperationOwner {
  readonly generation: number;
  readonly kind: 'bootstrap' | 'switch';
  readonly targetWorkspaceId: string | null;
  readonly subscriptions: Subscription[];
  lease: number;
}

interface SourceSettlement<T> {
  readonly next: (value: T) => void;
  readonly error: () => void;
  readonly empty: () => void;
}

export function createInitialWorkspaceBootstrapState(): WorkspaceBootstrapState {
  return freezeState({
    isLoading: false,
    isLoaded: false,
    errorMessage: '',
    user: null,
    workspaces: [],
    activeWorkspaceId: null,
    activeRole: null,
    activeWorkspaceType: null,
  });
}

export function createSystemAppWorkspaceContextEnvironment(): AppWorkspaceContextEnvironment {
  return Object.freeze({ now: () => Date.now() });
}

export class AppWorkspaceContextRuntime {
  private state = createInitialWorkspaceBootstrapState();
  private owner: OperationOwner | null = null;
  private queuedWorkspaceId: string | null = null;
  private generation = 0;
  private lastBootstrapFailureOn: number | null = null;
  private destroyed = false;

  constructor(
    private readonly port: AppWorkspaceContextPort,
    private readonly preferences: AppWorkspacePreferencePort,
    private readonly sink: AppWorkspaceContextSink,
    private readonly environment: AppWorkspaceContextEnvironment,
  ) {}

  snapshot(): WorkspaceBootstrapState {
    return this.state;
  }

  bootstrap(force = false): void {
    if (this.destroyed || this.owner?.kind === 'switch') return;
    if (
      this.owner ||
      (!force &&
        this.state.isLoaded &&
        this.state.user !== null &&
        this.state.errorMessage === '')
    ) {
      return;
    }

    const owner = this.beginOwner('bootstrap', null);
    const now = this.readClock();
    if (now === null) {
      this.fail(owner, LOAD_ERROR);
      return;
    }
    if (this.lastBootstrapFailureOn !== null) {
      if (now < this.lastBootstrapFailureOn) {
        this.lastBootstrapFailureOn = null;
      } else if (now - this.lastBootstrapFailureOn < RETRY_COOLDOWN_MS) {
        this.fail(owner, LOAD_ERROR, false);
        return;
      }
    }

    this.subscribeOnce(owner, () => this.port.fetchContext(), {
      next: (result) => this.acceptBootstrapResult(owner, result),
      error: () => this.fail(owner, LOAD_ERROR),
      empty: () => this.fail(owner, LOAD_ERROR),
    });
  }

  switchWorkspace(workspaceId: string): void {
    if (this.destroyed || !isExactUuid(workspaceId)) return;
    if (!hasActiveMembership(this.state.workspaces, workspaceId)) return;
    if (!this.owner && this.state.activeWorkspaceId === workspaceId) return;

    if (this.owner?.kind === 'switch') {
      if (this.owner.targetWorkspaceId === workspaceId) {
        this.queuedWorkspaceId = null;
      } else {
        this.queuedWorkspaceId = workspaceId;
        this.patch({ isLoading: true, errorMessage: '', ...NO_AUTHORITY });
      }
      return;
    }

    if (this.owner) this.releaseOwner(this.owner);
    this.startSwitch(workspaceId);
  }

  reset(): void {
    if (this.destroyed) return;
    this.queuedWorkspaceId = null;
    this.lastBootstrapFailureOn = null;
    const owner = this.owner;
    const generation = this.generation;
    if (owner) this.releaseOwner(owner);
    if (this.destroyed || this.owner || this.generation !== generation) return;
    this.publish(createInitialWorkspaceBootstrapState());
  }

  destroy(): void {
    if (this.destroyed) return;
    const owner = this.owner;
    this.destroyed = true;
    this.owner = null;
    this.generation += 1;
    this.queuedWorkspaceId = null;
    if (!owner) return;
    owner.lease += 1;
    const subscriptions = owner.subscriptions.splice(0);
    for (const subscription of subscriptions) safeUnsubscribe(subscription);
  }

  private acceptBootstrapResult(
    owner: OperationOwner,
    result: AppWorkspaceContextFetchResult,
  ): void {
    if (result.kind === 'unauthenticated') {
      this.acceptUnauthenticated(owner);
      return;
    }
    const context = decodeWorkspaceContext(result.response);
    if (!this.isCurrent(owner)) return;
    if (!context) {
      this.fail(owner, LOAD_ERROR);
      return;
    }
    if (
      context.currentWorkspaceId === null &&
      !context.workspaces.some(isActiveMembership)
    ) {
      this.acceptIdentityOnly(owner, context);
      return;
    }

    const preferredWorkspaceId = this.preferredWorkspaceId(context);
    if (!this.isCurrent(owner)) return;
    if (!preferredWorkspaceId) {
      this.fail(owner, LOAD_ERROR);
      return;
    }

    if (context.currentWorkspaceId === preferredWorkspaceId) {
      this.acceptContext(owner, context, preferredWorkspaceId);
      return;
    }

    this.patch({
      user: context.user,
      workspaces: context.workspaces,
      activeWorkspaceId: null,
      activeRole: null,
      activeWorkspaceType: null,
    });
    if (!this.isCurrent(owner)) return;
    this.releaseOwner(owner);
    if (this.owner) return;
    this.startSwitch(preferredWorkspaceId);
  }

  private startSwitch(workspaceId: string): void {
    const owner = this.beginOwner('switch', workspaceId);
    this.subscribeOnce(owner, () => this.port.switchWorkspace(workspaceId), {
      next: () => this.verifySwitch(owner),
      error: () => this.fail(owner, SWITCH_ERROR),
      empty: () => this.fail(owner, SWITCH_ERROR),
    });
  }

  private verifySwitch(owner: OperationOwner): void {
    if (!this.isCurrent(owner)) return;
    owner.lease += 1;
    this.subscribeOnce(owner, () => this.port.fetchContext(), {
      next: (result) => {
        const context =
          result.kind === 'context'
            ? decodeWorkspaceContext(result.response)
            : null;
        if (!this.isCurrent(owner)) return;
        const targetWorkspaceId = owner.targetWorkspaceId;
        if (
          !context ||
          !targetWorkspaceId ||
          context.currentWorkspaceId !== targetWorkspaceId
        ) {
          this.fail(owner, SWITCH_ERROR);
          return;
        }
        this.acceptContext(owner, context, targetWorkspaceId);
      },
      error: () => this.fail(owner, SWITCH_ERROR),
      empty: () => this.fail(owner, SWITCH_ERROR),
    });
  }

  private acceptContext(
    owner: OperationOwner,
    context: DecodedWorkspaceContext,
    workspaceId: string,
  ): void {
    if (!this.isCurrent(owner)) return;
    const workspace = context.workspaces.find(
      (candidate) => candidate.workspaceId === workspaceId,
    );
    if (!workspace || !isAcceptedMembership(workspace, context.user)) {
      this.fail(owner, owner.kind === 'switch' ? SWITCH_ERROR : LOAD_ERROR);
      return;
    }

    if (
      owner.kind === 'switch' &&
      this.queuedWorkspaceId &&
      this.queuedWorkspaceId !== workspaceId
    ) {
      this.continueQueuedSwitch(owner);
      return;
    }

    const acceptedState = freezeState({
      isLoading: false,
      isLoaded: true,
      errorMessage: '',
      user: context.user,
      workspaces: context.workspaces,
      activeWorkspaceId: workspaceId,
      activeRole: workspace.role ?? null,
      activeWorkspaceType: workspace.workspaceType ?? null,
    });
    this.queuedWorkspaceId = null;
    this.lastBootstrapFailureOn = null;
    this.publish(acceptedState);
    if (!this.isCurrent(owner)) return;
    if (this.queuedWorkspaceId) {
      this.continueQueuedSwitch(owner);
      return;
    }
    this.persistPreference(workspaceId);
    if (!this.isCurrent(owner)) return;
    if (this.queuedWorkspaceId) {
      this.continueQueuedSwitch(owner);
      return;
    }
    this.releaseOwner(owner);
  }

  private fail(owner: OperationOwner, message: string, record = true): void {
    if (!this.isCurrent(owner)) return;
    if (owner.kind === 'bootstrap' && record) {
      this.lastBootstrapFailureOn = this.readClock();
    } else if (owner.targetWorkspaceId) {
      this.removeRejectedPreference(owner.targetWorkspaceId);
    }
    if (!this.isCurrent(owner)) return;

    if (owner.kind === 'switch' && this.queuedWorkspaceId) {
      this.continueQueuedSwitch(owner);
      return;
    }

    this.publishFailure(message);
    if (!this.isCurrent(owner)) return;
    if (owner.kind === 'switch' && this.queuedWorkspaceId) {
      this.continueQueuedSwitch(owner);
      return;
    }
    this.releaseOwner(owner);
  }

  private continueQueuedSwitch(owner: OperationOwner): void {
    const queuedWorkspaceId = this.queuedWorkspaceId;
    this.queuedWorkspaceId = null;
    this.releaseOwner(owner);
    if (this.owner) return;
    if (
      queuedWorkspaceId &&
      hasActiveMembership(this.state.workspaces, queuedWorkspaceId)
    ) {
      this.startSwitch(queuedWorkspaceId);
    } else {
      this.publishFailure(SWITCH_ERROR);
    }
  }

  private beginOwner(
    kind: 'bootstrap' | 'switch',
    targetWorkspaceId: string | null,
  ): OperationOwner {
    const owner: OperationOwner = {
      generation: ++this.generation,
      kind,
      targetWorkspaceId,
      subscriptions: [],
      lease: 0,
    };
    this.owner = owner;
    this.patch({
      isLoading: true,
      errorMessage: '',
      ...NO_AUTHORITY,
    });
    return owner;
  }

  private publishFailure(message: string): void {
    this.patch({
      isLoading: false,
      isLoaded: true,
      errorMessage: message,
      ...NO_AUTHORITY,
    });
  }

  private acceptUnauthenticated(owner: OperationOwner): void {
    this.lastBootstrapFailureOn = null;
    this.publish(
      freezeState({
        ...createInitialWorkspaceBootstrapState(),
        isLoaded: true,
      }),
    );
    if (!this.isCurrent(owner)) return;
    this.persistPreference(null);
    if (this.isCurrent(owner)) this.releaseOwner(owner);
  }

  private acceptIdentityOnly(
    owner: OperationOwner,
    context: DecodedWorkspaceContext,
  ): void {
    this.lastBootstrapFailureOn = null;
    this.patch({
      isLoading: false,
      isLoaded: true,
      errorMessage: '',
      user: context.user,
      workspaces: context.workspaces,
      ...NO_AUTHORITY,
    });
    if (!this.isCurrent(owner)) return;
    this.persistPreference(null);
    if (this.isCurrent(owner)) this.releaseOwner(owner);
  }

  private preferredWorkspaceId(
    context: DecodedWorkspaceContext,
  ): string | null {
    const storedWorkspaceId = this.readPreference();
    if (
      storedWorkspaceId &&
      hasActiveMembership(context.workspaces, storedWorkspaceId)
    ) {
      return storedWorkspaceId;
    }
    if (storedWorkspaceId) this.removeRejectedPreference(storedWorkspaceId);
    if (
      context.currentWorkspaceId &&
      hasActiveMembership(context.workspaces, context.currentWorkspaceId)
    ) {
      return context.currentWorkspaceId;
    }
    if (
      context.defaultWorkspaceId &&
      hasActiveMembership(context.workspaces, context.defaultWorkspaceId)
    ) {
      return context.defaultWorkspaceId;
    }
    return (
      context.workspaces.find((workspace) => isActiveMembership(workspace))
        ?.workspaceId ?? null
    );
  }

  private subscribeOnce<T>(
    owner: OperationOwner,
    sourceFactory: () => Observable<T>,
    settlement: SourceSettlement<T>,
  ): void {
    if (!this.isCurrent(owner)) return;
    const lease = ++owner.lease;
    let seen = false;
    let source: Observable<T>;
    try {
      source = sourceFactory().pipe();
    } catch {
      settlement.error();
      return;
    }

    let subscription: Subscription;
    try {
      subscription = source.subscribe({
        next: (value) => {
          if (!this.ownsLease(owner, lease) || seen) return;
          seen = true;
          try {
            settlement.next(value);
          } catch {
            this.fail(
              owner,
              owner.kind === 'switch' ? SWITCH_ERROR : LOAD_ERROR,
            );
          }
        },
        error: () => {
          if (this.ownsLease(owner, lease)) settlement.error();
        },
        complete: () => {
          if (this.ownsLease(owner, lease) && !seen) settlement.empty();
        },
      });
    } catch {
      if (this.ownsLease(owner, lease)) settlement.error();
      return;
    }

    if (this.ownsLease(owner, lease)) owner.subscriptions.push(subscription);
    else safeUnsubscribe(subscription);
  }

  private releaseOwner(owner: OperationOwner): void {
    if (!this.isCurrent(owner)) return;
    this.owner = null;
    owner.lease += 1;
    const subscriptions = owner.subscriptions.splice(0);
    for (const subscription of subscriptions) safeUnsubscribe(subscription);
  }

  private ownsLease(owner: OperationOwner, lease: number): boolean {
    return this.isCurrent(owner) && owner.lease === lease;
  }

  private isCurrent(owner: OperationOwner): boolean {
    return (
      !this.destroyed &&
      this.owner === owner &&
      owner.generation === this.generation
    );
  }

  private readPreference(): string | null {
    try {
      const value = this.preferences.read();
      if (value === null || value === undefined) return null;
      if (isExactUuid(value)) return value;
      this.persistPreference(null);
      return null;
    } catch {
      return null;
    }
  }

  private removeRejectedPreference(workspaceId: string): void {
    try {
      if (this.preferences.read() === workspaceId) this.persistPreference(null);
    } catch {
      // A storage failure cannot change the accepted server authority.
    }
  }

  private persistPreference(workspaceId: string | null): void {
    try {
      if (workspaceId) this.preferences.write(workspaceId);
      else this.preferences.remove();
    } catch {
      // Preference storage is not an authority source.
    }
  }

  private readClock(): number | null {
    try {
      const value = this.environment.now();
      if (
        !Number.isFinite(value) ||
        !Number.isInteger(value) ||
        Math.abs(value) > MAX_TIME_CLIP
      ) {
        return null;
      }
      return new Date(value).getTime() === value ? value : null;
    } catch {
      return null;
    }
  }

  private publish(state: WorkspaceBootstrapState): void {
    if (this.destroyed) return;
    this.state = state;
    try {
      this.sink.publish(state);
    } catch {
      // The internal immutable snapshot remains authoritative for settlement.
    }
  }

  private patch(patch: Partial<WorkspaceBootstrapState>): void {
    this.publish(freezeState({ ...this.state, ...patch }));
  }
}

function decodeWorkspaceContext(
  response: AppWorkspaceContextResponse,
): DecodedWorkspaceContext | null {
  try {
    if (!response) return null;
    const sourceUser = response.user;
    const sourceWorkspaces = response.workspaces;
    if (!Array.isArray(sourceWorkspaces)) return null;
    const user = cloneUser(sourceUser);
    if (!user) return null;
    const workspaces = sourceWorkspaces.map(cloneWorkspace);
    if (workspaces.some((workspace) => workspace === null)) return null;
    const acceptedWorkspaces = workspaces.filter(
      (workspace): workspace is WorkspaceBootstrapWorkspace =>
        workspace !== null,
    );
    const workspaceIds = new Set<string>();
    let defaultWorkspaceId: string | null = null;
    for (const workspace of acceptedWorkspaces) {
      const workspaceId = workspace.workspaceId;
      if (!workspaceId || workspaceIds.has(workspaceId)) return null;
      workspaceIds.add(workspaceId);
      if (workspace.isDefaultWorkspace) {
        if (defaultWorkspaceId) return null;
        defaultWorkspaceId = workspaceId;
      }
    }

    const currentWorkspaceId = user.workspaceId ?? null;
    if (currentWorkspaceId) {
      const current = acceptedWorkspaces.find(
        (workspace) => workspace.workspaceId === currentWorkspaceId,
      );
      if (!current || !isAcceptedMembership(current, user)) return null;
    } else if (user.role !== null) {
      return null;
    }

    return Object.freeze({
      user,
      workspaces: Object.freeze(acceptedWorkspaces),
      currentWorkspaceId,
      defaultWorkspaceId,
    });
  } catch {
    return null;
  }
}

function cloneUser(value: UserContextResponse): WorkspaceBootstrapUser | null {
  if (!value) return null;
  const userId = value.userId;
  const email = value.email;
  const phone = value.phone;
  const username = value.username;
  const displayName = value.displayName;
  const firstName = value.firstName;
  const lastName = value.lastName;
  const avatarUrl = value.avatarUrl;
  const sessionId = value.sessionId;
  const workspaceId: unknown = value.workspaceId;
  const role: unknown = value.role;
  const isAuthenticated = value.isAuthenticated;
  const settings = cloneSettings(value.settings);
  if (
    !isExactUuid(userId) ||
    !isExactUuid(sessionId) ||
    isAuthenticated !== true ||
    !isExactText(email) ||
    !isExactText(username) ||
    !isExactText(displayName) ||
    !isNullableExactText(phone) ||
    !isNullableExactText(firstName) ||
    !isNullableExactText(lastName) ||
    !isNullableExactText(avatarUrl) ||
    settings === null
  ) {
    return null;
  }
  const projection = {
    userId,
    email,
    phone,
    username,
    displayName,
    firstName,
    lastName,
    avatarUrl,
    sessionId,
    isAuthenticated,
    settings,
  };
  if (workspaceId === EMPTY_UUID) {
    return role === null
      ? Object.freeze({ ...projection, workspaceId: null, role: null })
      : null;
  }
  if (!isExactUuid(workspaceId) || !isWorkspaceRole(role)) return null;
  return Object.freeze({ ...projection, workspaceId, role });
}

function cloneSettings(
  value: UserSettingsResponse | undefined,
): WorkspaceBootstrapUserSettings | null {
  if (!value) return null;
  const agentConfirmationMode = value.agentConfirmationMode;
  return isAgentConfirmationMode(agentConfirmationMode)
    ? Object.freeze({ agentConfirmationMode })
    : null;
}

function cloneWorkspace(
  value: WorkspaceSummaryResponse,
): WorkspaceBootstrapWorkspace | null {
  const workspaceId = value.workspaceId;
  const slug = value.slug;
  const name = value.name;
  const workspaceType = value.workspaceType;
  const role = value.role;
  const status = value.status;
  const defaultAutonomyMode = value.defaultAutonomyMode;
  const timeZone = value.timeZone;
  const isActive = value.isActive;
  const isDefaultWorkspace = value.isDefaultWorkspace;
  const joinedOn = value.joinedOn;
  if (
    !isExactUuid(workspaceId) ||
    !isWorkspaceRole(role) ||
    !isWorkspaceKind(workspaceType) ||
    !isWorkspaceStatus(status) ||
    !isWorkspaceAutonomyMode(defaultAutonomyMode) ||
    typeof isActive !== 'boolean' ||
    typeof isDefaultWorkspace !== 'boolean' ||
    !isExactText(slug) ||
    !isExactText(name) ||
    !isExactText(timeZone) ||
    !isExactTimestamp(joinedOn)
  ) {
    return null;
  }
  return Object.freeze({
    workspaceId,
    slug,
    name,
    workspaceType,
    role,
    status,
    defaultAutonomyMode,
    timeZone,
    isActive,
    isDefaultWorkspace,
    joinedOn,
  });
}

function isAcceptedMembership(
  workspace: WorkspaceBootstrapWorkspace,
  user: WorkspaceBootstrapUser,
): boolean {
  return (
    user.isAuthenticated === true &&
    user.workspaceId === workspace.workspaceId &&
    user.role === workspace.role &&
    isActiveMembership(workspace)
  );
}

function isActiveMembership(workspace: WorkspaceBootstrapWorkspace): boolean {
  return (
    workspace.status === WorkspaceMemberStatus.Active &&
    workspace.isActive === true
  );
}

function hasActiveMembership(
  workspaces: readonly WorkspaceBootstrapWorkspace[],
  workspaceId: string,
): boolean {
  return workspaces.some(
    (workspace) =>
      workspace.workspaceId === workspaceId && isActiveMembership(workspace),
  );
}

function isExactUuid(value: unknown): value is string {
  return (
    isCanonicalUuid(value) &&
    value === value.toLowerCase() &&
    value === value.trim()
  );
}

const workspaceRoles: readonly WorkspaceRole[] = Object.freeze(
  Object.values(WorkspaceRole),
);
const workspaceKinds: readonly WorkspaceKind[] = Object.freeze(
  Object.values(WorkspaceKind),
);
const workspaceStatuses: readonly WorkspaceMemberStatus[] = Object.freeze(
  Object.values(WorkspaceMemberStatus),
);
const workspaceAutonomyModes: readonly WorkspaceAutonomyMode[] = Object.freeze(
  Object.values(WorkspaceAutonomyMode),
);

function isWorkspaceRole(value: unknown): value is WorkspaceRole {
  return workspaceRoles.some((candidate) => candidate === value);
}

function isWorkspaceKind(value: unknown): value is WorkspaceKind {
  return workspaceKinds.some((candidate) => candidate === value);
}

function isWorkspaceStatus(value: unknown): value is WorkspaceMemberStatus {
  return workspaceStatuses.some((candidate) => candidate === value);
}

function isWorkspaceAutonomyMode(
  value: unknown,
): value is WorkspaceAutonomyMode {
  return workspaceAutonomyModes.some((candidate) => candidate === value);
}

function isExactText(value: unknown): value is string {
  return (
    typeof value === 'string' && value.length > 0 && value === value.trim()
  );
}

function isNullableExactText(value: unknown): value is string | null {
  return value === null || isExactText(value);
}

function isAgentConfirmationMode(
  value: unknown,
): value is WorkspaceAgentConfirmationMode {
  return (
    value === 'Ask' ||
    value === 'AskThenAutoProceedIfNoResponse' ||
    value === 'ProceedAutomatically'
  );
}

function isExactTimestamp(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = isoOffsetTimestampPattern.exec(value);
  if (!match) return false;
  const [year, month, day, hour, minute, second] = match
    .slice(1, 7)
    .map(Number);
  const offset = match[7] ?? '+00:00';
  const offsetHour = Number(offset.slice(1, 3));
  const offsetMinute = Number(offset.slice(4, 6));
  const maximumDay = daysInMonth(year, month);
  if (!(
    year > 0 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= maximumDay &&
    hour < 24 &&
    minute < 60 &&
    second < 60 &&
    offsetMinute < 60 &&
    (offsetHour < 14 || (offsetHour === 14 && offsetMinute === 0))
  )) {
    return false;
  }
  const localSeconds =
    (daysBeforeYear(year) + daysBeforeMonth(year, month) + day - 1) * 86400 +
    hour * 3600 +
    minute * 60 +
    second;
  const offsetSeconds =
    (offset.startsWith('-') ? -1 : 1) * (offsetHour * 3600 + offsetMinute * 60);
  const utcSeconds = localSeconds - offsetSeconds;
  const maximumUtcSecond = daysBeforeYear(10000) * 86400 - 1;
  return utcSeconds >= 0 && utcSeconds <= maximumUtcSecond;
}

function daysBeforeYear(year: number): number {
  const completedYears = year - 1;
  return (
    completedYears * 365 +
    Math.floor(completedYears / 4) -
    Math.floor(completedYears / 100) +
    Math.floor(completedYears / 400)
  );
}

function daysBeforeMonth(year: number, month: number): number {
  let days = 0;
  for (let candidate = 1; candidate < month; candidate += 1) {
    days += daysInMonth(year, candidate);
  }
  return days;
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  }
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}

function freezeState(state: WorkspaceBootstrapState): WorkspaceBootstrapState {
  if (!Object.isFrozen(state.workspaces)) Object.freeze(state.workspaces);
  return Object.freeze(state);
}

function safeUnsubscribe(subscription: Subscription): void {
  try {
    subscription.unsubscribe();
  } catch {
    // Ownership is already invalidated before teardown.
  }
}
